import { type SanityImageSource } from "@sanity/image-url/lib/types/types";
import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type IntegrationSimple } from "./integrations";

export type PlaybookAuthor = {
  name: string;
  url?: string;
};

export type PlaybookSimple = {
  title: string;
  slug: string;
  imageUrl?: SanityImageSource;
  excerpt: string;
};

export const playbookSimpleFragment = "title, slug, 'imageUrl': image.asset->url, excerpt";

export type Extension = {
  name: string;
  slug: string;
  iconUrl: string;
};

export type Playbook = {
  title: string;
  slug: string;
  imageUrl: string;
  integrations: IntegrationSimple[];
  extensions?: Extension[];
  excerpt: string;
  content: string;
  authors: PlaybookAuthor[];
  experienceLevel: string;
  industry: string;
  team: string;
  related?: RelatedPlaybook[];
  metaTitle?: string;
  metaDescription?: string;
  _createdAt: string;
  _updatedAt: string;
};

export type RelatedPlaybook = {
  slug: string;
  title: string;
  authors: PlaybookAuthor[];
  excerpt: string;
  integrations: IntegrationSimple[];
  extensions?: Extension[];
  team: string;
  industry: string;
  experienceLevel: string;
};

const relatedPlaybookFragment = `
  slug,
  title,
  'authors': authors[]->{ name, url },
  excerpt,
  integrations[]->{ name, slug, 'iconUrl': icon.asset->url },
  extensions[]->{ name, slug, 'iconUrl': icon.asset->url },
  'team': team->name,
  'industry': industry->name,
  experienceLevel,
`;

export const getPlaybookPost = async (slug: string): Promise<Playbook | undefined> => {
  const query = groq`
    *[_type == "playbook" && slug == $slug][0] {
      title,
      slug,
      'imageUrl': image.asset->url,
      integrations[]->{ name, slug, 'iconUrl': icon.asset->url },
      extensions[]->{ name, slug, 'iconUrl': icon.asset->url },
      excerpt,
      content,
      experienceLevel,
      'industry': industry->name,
      'team': team->name,
      'authors': authors[]->{ name, url },
      related[]->{ ${relatedPlaybookFragment} },
      metaTitle,
      metaDescription,
      _createdAt,
      _updatedAt,
    }
  `;

  try {
    const parameters = { slug };
    const playbookPost = await getClient().fetch(query, parameters);
    return playbookPost;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch playbook with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};
